'use client'

import type { CSSProperties } from 'react'

import { ImageWrapper } from '@/components/image'
import { popupEventName } from '@/lib/popups/constants'
import { cx } from '@/lib/utils'

import { popupClickTracking } from './click-tracking'
import type { PopupTemplateProps } from './template-props'

// Structured promo card: title + body + CTA button on the left, image on the
// right (the Yeasound-style GSC custom widget). Copy comes from the DB; the
// styling lives here — one-off designs become a new template component, not
// markup in the DB.
export const CardPopup = ({ popup, onCtaClick, reason }: PopupTemplateProps) => {
  const linkProps = {
    href: popup.url,
    target: popup.newTab ? '_blank' : undefined,
    rel: 'noopener noreferrer nofollow sponsored',
    ...popupClickTracking(reason),
    'data-event-name': popupEventName(popup.slug),
    'data-event-position': popup.slug,
    onClick: onCtaClick,
  }

  // `imageSide` is the card's own layout choice — not `position`, which only
  // says where the popup is anchored on screen. Bleed runs the image to the
  // card edges instead of insetting it, toward whichever side it sits on.
  const bleed = popup.imageBleed
  const imageLeft = popup.imageSide === 'left'
  const imagePercent = popup.imageColumnPercent
  const copyPercent = 100 - imagePercent
  const columnStyle = {
    '--popup-card-columns': imageLeft ? `${imagePercent}fr ${copyPercent}fr` : `${copyPercent}fr ${imagePercent}fr`,
  } as CSSProperties

  return (
    <div
      className={cx(
        // 5rem of side gutter, wider than the frame's 3rem and the image
        // teaser's, because this card *fills* its box where the teaser is
        // w-fit: without a tighter bound it renders edge-to-edge and a square
        // creative's height follows its width, swallowing the screen. Desktop
        // is unaffected — max-w-[560px] takes over long before the gutter does.
        'ring-navy/10 w-[calc(100vw-5rem)] max-w-[560px] rounded-3xl bg-white p-6 pt-5 shadow-2xl ring-1 md:p-8 md:pt-6',
        // Clips the bled image back to the card's radius. Safe for the close
        // button, which is a sibling in PopupFrame, not a child of this card.
        bleed && 'overflow-hidden'
      )}
    >
      {/* Mobile stacks (image on top, full width); md+ is the two-column row. */}
      <div
        style={columnStyle}
        className={cx(
          'flex flex-col gap-6',
          popup.imageUrl && 'md:grid md:grid-cols-[var(--popup-card-columns)] md:gap-8',
          // items-center centres a short inset image; a bled one must stretch
          // to the row's full height to reach the top and bottom corners.
          bleed ? 'md:items-stretch' : 'md:items-center'
        )}
      >
        <div className={cx('min-w-0', imageLeft ? 'md:col-start-2 md:row-start-1' : 'md:col-start-1 md:row-start-1')}>
          {popup.title ? (
            <h2 className="text-navy pr-8 text-2xl font-bold tracking-tight md:text-xl">{popup.title}</h2>
          ) : null}
          {popup.body ? (
            <p className="text-navy/75 md:text-base-minus mt-2 text-base leading-snug">{popup.body}</p>
          ) : null}
          {popup.ctaLabel ? (
            <a
              {...linkProps}
              className="bg-navy hover:bg-navy-90 text-md mt-5 block cursor-pointer rounded-lg px-6 py-3 text-center font-bold text-white shadow-md transition-colors md:text-sm"
            >
              {popup.ctaLabel}
            </a>
          ) : null}
        </div>
        {popup.imageUrl ? (
          <a
            {...linkProps}
            tabIndex={-1}
            className={cx(
              // Mobile always stacks the image on top; imageSide only decides
              // the md+ column order.
              'order-first shrink-0 cursor-pointer',
              imageLeft ? 'md:col-start-1 md:row-start-1' : 'md:col-start-2 md:row-start-1',
              bleed
                ? // Negative margins cancel the card's own padding, per side and
                  // per breakpoint (base p-6/pt-5, md p-8/pt-6). Written out
                  // side-by-side rather than as -mx shorthand so the md rules
                  // can reset the inner side back to 0 without fighting it.
                  //
                  // w-auto, NOT w-full: an item with an explicit width:100%
                  // does not grow when given negative margins — it keeps its
                  // 100% and just shifts. Let the layout's stretch sizing make
                  // the negative outer margin widen the image box instead.
                  cx(
                    'w-auto -mt-5 -mr-6 -ml-6 [&_img]:w-full [&_img]:object-cover',
                    'md:-mt-6 md:-mb-8 md:h-auto md:[&_img]:h-full',
                    imageLeft ? 'md:mr-0 md:-ml-8' : 'md:ml-0 md:-mr-8'
                  )
                : 'w-full px-4 pt-2 max-xs:[&_img]:mx-auto max-xs:[&_img]:max-h-[300px] max-xs:[&_img]:w-auto md:self-center md:p-0'
            )}
          >
            <ImageWrapper
              image={popup.imageUrl}
              imgProps={{ alt: popup.imageAlt ?? '', loading: 'lazy', decoding: 'async' }}
              sizingProps={
                // A bled image is cropped to fill its box, so the intrinsic
                // ratio is not the layout box — passing aspectRatio here would
                // reserve a shape that fights object-cover. The stretched
                // container defines the height instead.
                !bleed && popup.imageWidth && popup.imageHeight && popup.imageHeight > 0
                  ? { maxWidth: 560, aspectRatio: popup.imageWidth / popup.imageHeight }
                  : { maxWidth: 560 }
              }
              origin="site-popup"
              loadTime="lazy"
              blockRobots
            />
          </a>
        ) : null}
      </div>
    </div>
  )
}
